import { Store } from '@ngxs/store';
import {MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { BatchTranslatePayload, SectionalLanguageList, TranslationsList } from '../../../model';
import { ShowSnackbarError, ShowSnackbarSuccess, TranslationTableState } from '../../state';
import { TranslationEditorService } from '../../service';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import {MatIcon} from "@angular/material/icon";

@Component({
  selector: 'app-batch-translate',
  templateUrl: './batch-translate.component.html',
  styleUrls: ['./batch-translate.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LanguageSelectorComponent, MatDialogModule, MatIcon],
})
export class BatchTranslateComponent {
  private readonly store = inject(Store);
  private readonly translationEditorService = inject(TranslationEditorService);
  private readonly dialogRef = inject<MatDialogRef<BatchTranslateComponent>>(MatDialogRef);

  subscription: Subscription;
  selectedLanguages: Array<string> = [];

  /**
   * Event callback which listens to the language selection change event and correspondingly updates the local object
   * selectedLanguages
   * @param languages Object is a collection of selected languages as per the sections main and additional
   */
  onLanguageSelectionChange(languages: SectionalLanguageList): void {
    this.selectedLanguages = Object.keys(languages)
      .map(section => languages[section].map(language => language.lnCode))
      .flat();
  }

  /**
   * Function invoked for closing the dialog.
   * @param updatedTranslations Optional parameter passed back to the parent component to update the original
   * list of translations.
   */
  onCloseClick(updatedTranslations?: TranslationsList): void {
    this.dialogRef.close(updatedTranslations);
  }

  /**
   * Function is invoked when the translate button is clicked. Makes the backend call to batch translate the selected translations
   * for the set of selected languages.
   * On success shows a success message and closes the dialog with the data to be updated.
   * On failure, shows the error message and retains the instance of the dialog.
   */
  onTranslateClick(): void {
    const selectedTranslationKeys = this.store
      .selectSnapshot(TranslationTableState.selectedTranslations)
      .map(translation => translation.key);
    const payload: BatchTranslatePayload = { targetLangs: this.selectedLanguages, keys: selectedTranslationKeys };
    this.subscription = this.translationEditorService.batchTranslate(payload).subscribe({
      next: (response: TranslationsList) => {
        this.store.dispatch(new ShowSnackbarSuccess(`keys translated for selected languages`));
        this.onCloseClick(response);
      },
      error: (err: HttpErrorResponse) =>
        this.store.dispatch(new ShowSnackbarError(`could not translate selected keys`, { value: err?.message })),
    });
  }
}
